import { useMutation } from "@apollo/client";
import { Button, Grid } from "@material-ui/core";
import Dialog from "components/Dialog";
import { LinearBuffer } from "components/LinearbBuffer";
import { removeFormatNumber } from "config/constants";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { SubTaskByStatusAndClientID } from "domains/Dashboard/graphql/queries";
import { NewJobContext } from "domains/Job/context";
import { recapStyles } from "domains/Task/components/Job/styles";
import { createJob, UpdateJobPickup } from "domains/Task/graphql/mutations";
import { Job, JobType, Order, Parcel, Receiver } from "generated/graphql";
import { useCurrentClient } from "hooks";
import { useCountryCode, useDeliverySlots } from "hooks/useCurrentUser";
import { isInteger } from "lodash";
import React from "react";
import "styled-components/macro";
import { formatPhoneNumber, generateAirwayBills } from "utils";
import { GlobalsButtonsRecap } from "./JobStyled";
import OrderCollapse from "./OrderCollapse";

export const asInteger = (value: any) => (isInteger(value) ? value : Number.parseInt(value, 10));

export const formatParcel = (parcel: any, isUpdate?: boolean) => ({
  parcel_value: asInteger(parcel?.parcel_value),
  parcel_amount_to_collect: asInteger(parcel?.parcel_amount_to_collect),
  parcel_description: parcel?.parcel_description,
  parcel_size: parcel?.parcel_size,
  parcel_client_reference: parcel?.parcel_client_reference,
  parcel_UID: isUpdate ? parcel?.parcel_UID : undefined,
  parcel_additional_info: parcel?.parcel_additional_info,
  pickupAddress: parcel?.pickupAddress
    ? {
        place_id: parcel?.pickupAddress?.address?.place_id,
      }
    : null,
  pickupMoreInfo: parcel?.pickupMoreInfo ? parcel?.pickupMoreInfo : null,
});

export interface RecapProps {
  setActiveStep?: React.Dispatch<React.SetStateAction<number>>;
  setParcels?: React.Dispatch<React.SetStateAction<Parcel[]>>;
  activeStep?: number;
}
export function JobReacap({ setActiveStep, activeStep, setParcels }: RecapProps) {
  const styles = recapStyles();
  const countryCode = useCountryCode();
  const { client } = useCurrentClient();
  const { job, setJob, orders, userData } = React.useContext(NewJobContext);
  const [jobView, setJobView] = React.useState<Job>(null);
  const [confirm, setConfirm] = React.useState(false);
  const [submitted, toSubmit] = React.useState(false);
  const dispatchAlert = useAlertDispatch();
  const [addJob] = useMutation(createJob);
  const [currentOrder, setCurrentOrder] = React.useState(null);
  const [updateJobPickup] = useMutation(UpdateJobPickup, {
    refetchQueries: [
      {
        query: SubTaskByStatusAndClientID,
        variables: {
          params: { clientID: client?._id },
          queries: { date: job?.job_date },
        },
      },
    ],
  });
  const { text } = useText();
  const {
    morningSlotStart,
    morningSlotEnd,
    afternoonSlotStart,
    afternoonSlotEnd,
    allDaySlotStart,
    allDaySlotEnd,
  } = useDeliverySlots() || {};
  
  const parcelsJobOrdersResponses = ({ job_orders }: Job) => {
    const parcels: Parcel[] = job_orders.reduce((list: Parcel[], current: Order) => {
      const orders_parcles = current.order_parcels.map((item) => ({
        ...item,
        parcel_order: { order_receiver: current.order_receiver },
      }));
      return [...list, ...orders_parcles];
    }, []);
    return parcels;
  };

  const formatReceiver = (receiver: Receiver) => ({
    receiver_email: receiver.receiver_email,
    receiver_lastname: receiver.receiver_lastname,
    receiver_firstname: receiver.receiver_firstname,
    receiver_entreprise: receiver?.receiver_entreprise ?? null,
    receiver_specification_address: receiver?.receiver_specification_address ?? undefined,
    receiver_phone_number: removeFormatNumber(formatPhoneNumber(receiver.receiver_phone_number)),
    receiver_address: { place_id: receiver.receiver_address.place_id },
  });

  const createParcels = () => {
    setConfirm(false);
    toSubmit(true);
    const jobPayload: Job = {
      job_type: job?.job_type,
      job_date: job?.job_date,
      job_time: job?.job_time,
    };
    if (JobType.Pickup === job?.job_type) {
      jobPayload.job_address = { place_id: job?.job_address?.place_id };
      jobPayload.job_vehicle_type = job?.job_vehicle_type;
    }
    if (jobPayload.job_time === `${morningSlotStart}:00`) {
      jobPayload.job_slot_start = `${morningSlotStart}:00`;
      jobPayload.job_slot_end = `${morningSlotEnd}:00`;
    } else if (jobPayload.job_time === `${afternoonSlotStart}:00`) {
      jobPayload.job_slot_start = `${afternoonSlotStart}:00`;
      jobPayload.job_slot_end = `${afternoonSlotEnd}:00`;
    } else if (jobPayload.job_time === `${allDaySlotStart}:00`) {
      jobPayload.job_slot_start = `${allDaySlotStart}:00`;
      jobPayload.job_slot_end = `${allDaySlotEnd}:00`;
    }
    const inputOrder: any[] = orders.map((order: Order) => {
      const { order_parcels, order_receiver, relay } = order;
      return {
        parcels: order_parcels.map((item) => formatParcel(item)),
        receiver: formatReceiver(order_receiver),
        relay: relay?._id,
      };
    });
    addJob({
      variables: {
        input: {
          orders: inputOrder,
          clientID: client?._id ?? userData?.company_user_clientId,
          job: jobPayload,
        },
      },
    })
      .then((result) => {
        const { code, message, data } = result.data.addJob;
        if (code === 201) {
          const parcels = parcelsJobOrdersResponses(data.job);
          generateAirwayBills(parcels, job, client, countryCode);
          setParcels(parcels);
          toSubmit(false);
          setActiveStep(3);
          dispatchAlert({ message, severity: "success" });
        } else {
          toSubmit(false);
          dispatchAlert({ message, severity: "error" });
        }
      })
      .catch((err) => {
        toSubmit(false);
        dispatchAlert({ message: "Your new job is not created...", severity: "error" });
      });
  };
  React.useEffect(() => {
    setJobView(job);
  }, [job]);

  const updateParcels = () => {
    setConfirm(false);
    toSubmit(true);
    const inputOrder: any[] = orders.map((order) => {
      const { order_parcels, order_receiver } = order;
      return {
        parcels: order_parcels.map((item) => formatParcel(item, true)),
        receiver: {
          ...formatReceiver(order_receiver),
          receiver_UID: order_receiver?.receiver_phone_number ? order_receiver?._id : null,
        },
        order_UID: order?.order_UID ?? null,
      };
    });

    updateJobPickup({
      variables: {
        input: {
          orders: inputOrder,
          clientID: client?._id ?? userData?.company_user_clientId,
          id: userData?._id,
        },
      },
    })
      .then((result) => {
        const { code, message, data } = result.data?.updateJobPickup;
        if (code === 201) {
          const parcels = parcelsJobOrdersResponses(data.job);
          setParcels(parcels);
          toSubmit(false);
          setJob(null);
          setActiveStep(3);
          dispatchAlert({ message, severity: "success" });
        } else {
          toSubmit(false);
          dispatchAlert({ message, severity: "error" });
        }
      })
      .catch(() => {
        toSubmit(false);
        dispatchAlert({ message: "Your job is not updated...", severity: "error" });
      });
  };

  const handleClose = () => {
    setConfirm(false);
  };

  const handleConfirm = () => {
    setConfirm(true);
  };

  const onClickOrder = (order: Order) => {
    setCurrentOrder(order);
  };

  return (
    <div className={styles.root}>
      <div className={styles.rootContent}>
        <Grid className={styles.top}>
          <Grid className={styles.orderGlobal}>
            <h3 className={styles.orderJobType}>
              {job.job_type === JobType.FromStock && text.all.from_stock_label}              
              {job.job_type === JobType.Dropoff && text.all.drop_off_label}
              {job.job_type === JobType.Pickup && text.all.pickup_label}
              </h3>
            <h3 className={styles.orderItem}>{orders.length} {text.all.order}</h3>
            <hr className={styles.hrOrder} />
          </Grid>

          <Grid className={styles.client}>
            <p>{text.all.sender}: </p>
            <p>
              {client.client_contact_firstname} {client.client_contact_lastname} - {client.client_phone_number}
            </p>
            <p>{jobView?.job_address?.address}</p>
            <hr className={styles.hr} />
          </Grid>

          <Grid className={styles.jobTime}>
            <p className={styles.date}>
              Date: <span>{job.job_date}</span>
            </p>
            <p className={styles.date}>
              {text.all.time}: <span>{job.job_time}</span>
            </p>
          </Grid>
        </Grid>

        {submitted ? (
          <LinearBuffer />
        ) : (
          <>
            {orders &&
              orders.map((order: Order, index: number) => (
                <div
                  key={index}
                  style={{
                    paddingTop: 10,
                    paddingBottom: 10,
                    paddingRight: 10,
                  }}
                  onClick={() => onClickOrder(order)}
                >
                  <OrderCollapse activeStep={activeStep} order={order} key={index} />
                </div>
              ))}
          </>
        )}
      </div>
      <GlobalsButtonsRecap>
        <Button color="primary" variant="outlined" className={styles.previousRecapJob} onClick={() => setActiveStep(1)}>
          {text.all.previous}
        </Button>
        <Button
          color="primary"
          variant="contained"
          className={styles.confirmRecapJob}
          onClick={handleConfirm}
          disabled={submitted}
        >
          {text.all.confirm}
        </Button>
      </GlobalsButtonsRecap>
      <Dialog
        className={styles.dialogConfirm}
        title={text.all.confirmation}
        open={confirm}
        confirmButtonTitle={text.all.confirm}
        handleClose={handleClose}
        handleConfirm={createParcels}
        height="33vh"
      >
        {text.all.are_you_done_creating_parcel}
      </Dialog>
    </div>
  );
}
